import type { AppLanguage } from '../i18n/config';
import { PATHS } from '../router/paths';

type PageKey =
  | 'home'
  | 'services'
  | 'pricing'
  | 'about'
  | 'contact'
  | 'projects'
  | 'privacy'
  | 'cookies'
  | 'legalInfo'
  | 'terms'
  | 'notFound';

type PageSeoCopy = {
  title: string;
  description: string;
  keywords: string[];
  breadcrumb: string;
};

type SeoData = {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  image: string;
  robots: string;
  ogLocale: string;
  structuredData: Record<string, unknown>[];
};

const BRAND_NAME = 'Bc. Kamil Frajtko';
const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const NOT_FOUND_ROBOTS = 'noindex, nofollow, noarchive';
const SITE_URL = normalizeSiteUrl(__SITE_URL__ || 'https://kamilfrajtko.sk');
const BASE_PATH = normalizeBasePath(__BASE_PATH__);
const SOCIAL_LINKS = [
  'https://www.instagram.com/kamilfrajtko',
  'https://www.linkedin.com/in/kamilfrajtko/',
];
const SPECIALIZATIONS = [
  'Weby pre salóny, barberstvá a beauty prevádzky',
  'Weby pre autoservisy, pneuservisy a umyvárky',
  'Weby pre reštaurácie, kaviarne a gastro prevádzky',
  'Weby pre hotely, penzióny a apartmány',
  'Weby pre kliniky, ambulancie a zdravotné služby',
  'Weby pre reality, developerov a architektúru',
  'Weby pre stavebné firmy, remeslá a výrobu',
  'Weby pre fitness, šport a wellness segment',
  'Weby pre kurzy, školenia a vzdelávanie',
  'Weby pre advokátov, účtovníkov a konzultantov',
  'E-shopy a online predaj',
  'Firemné weby, interné systémy a automatizácia procesov',
  'Rezervačné systémy, formuláre a workflow',
];

const LOCALE_CODES: Record<AppLanguage, string> = {
  sk: 'sk_SK',
  cs: 'cs_CZ',
  en: 'en_US',
  de: 'de_DE',
  pl: 'pl_PL',
};

const HOME_LABELS: Record<AppLanguage, string> = {
  sk: 'Domov',
  cs: 'Domů',
  en: 'Home',
  de: 'Startseite',
  pl: 'Start',
};

const PROFESSIONAL_SERVICE_LABELS: Record<AppLanguage, string> = {
  sk: 'Digitálny konzultant a vývojár webových riešení',
  cs: 'Digitální konzultant a vývojář webových řešení',
  en: 'Digital consultant and web solutions developer',
  de: 'Digitalberater und Entwickler von Weblösungen',
  pl: 'Konsultant cyfrowy i twórca rozwiązań webowych',
};

const SEO_COPY: Record<AppLanguage, Record<PageKey, PageSeoCopy>> = {
  sk: {
    home: {
      title: 'Tvorba webov a e-shopov na mieru | Bc. Kamil Frajtko',
      description:
        'Tvorba webov, e-shopov, automatizácií a digitálnych riešení na mieru pre firmy, ktoré chcú funkčný web, online predaj a lepšie procesy.',
      keywords: [
        'tvorba webov',
        'tvorba e-shopov',
        'web na mieru',
        'automatizácia procesov',
        'digitálne riešenia na mieru',
        'programovanie webov',
      ],
      breadcrumb: 'Domov',
    },
    services: {
      title: 'Služby: weby, e-shopy a automatizácie | Bc. Kamil Frajtko',
      description:
        'Weby na mieru, e-shopy, automatizácie a digitálne riešenia navrhnuté tak, aby fungovali po technickej aj biznisovej stránke.',
      keywords: [
        'weby na mieru',
        'e-shopy',
        'automatizácie',
        'digitálne riešenia',
        'interné systémy',
        'redizajn webu',
      ],
      breadcrumb: 'Služby',
    },
    pricing: {
      title: 'Orientačné ceny tvorby webov a e-shopov | Bc. Kamil Frajtko',
      description:
        'Orientačné štartovacie ceny pre weby, e-shopy, automatizácie a riešenia na mieru. Každý projekt naceňujem individuálne podľa rozsahu a cieľa.',
      keywords: [
        'cenník tvorby webu',
        'cena e-shopu',
        'orientačné ceny webu',
        'cena webu na mieru',
        'automatizácia cena',
      ],
      breadcrumb: 'Orientačné ceny',
    },
    about: {
      title: 'O mne | Bc. Kamil Frajtko',
      description:
        'Bc. Kamil Frajtko, digitálny konzultant a developer. Spájam technické riešenie, funkčný dizajn a biznisový pohľad.',
      keywords: [
        'Bc. Kamil Frajtko',
        'digitálny konzultant',
        'web developer',
        'freelancer programátor',
        'o mne',
      ],
      breadcrumb: 'O mne',
    },
    contact: {
      title: 'Kontakt | Bc. Kamil Frajtko',
      description:
        'Kontaktujte ma pre web, e-shop, automatizáciu alebo riešenie na mieru. Odpoviem spravidla do 1 až 2 pracovných dní.',
      keywords: [
        'kontakt web developer',
        'dopyt web na mieru',
        'kontakt e-shop',
        'kontakt automatizácia',
      ],
      breadcrumb: 'Kontakt',
    },
    projects: {
      title: 'Vybrané projekty webov a e-shopov | Bc. Kamil Frajtko',
      description:
        'Výber webov, e-shopov a digitálnych riešení, na ktorých som pracoval. Ukážky projektov, dizajnu a funkcionalít.',
      keywords: [
        'portfólio web developer',
        'projekty weby',
        'ukážky e-shopov',
        'referencie weby',
      ],
      breadcrumb: 'Vybrané projekty',
    },
    privacy: {
      title: 'Ochrana osobných údajov | Bc. Kamil Frajtko',
      description:
        'Informácie o spracúvaní osobných údajov, kontaktnom formulári, analytike a právach dotknutých osôb podľa GDPR.',
      keywords: ['ochrana osobných údajov', 'GDPR', 'spracúvanie údajov', 'kontakt formulár'],
      breadcrumb: 'Ochrana osobných údajov',
    },
    cookies: {
      title: 'Cookies | Bc. Kamil Frajtko',
      description:
        'Informácie o cookies, analytike Google Analytics 4 a možnostiach správy súhlasu na tomto webe.',
      keywords: ['cookies', 'google analytics', 'súhlas s cookies', 'nastavenie cookies'],
      breadcrumb: 'Cookies',
    },
    legalInfo: {
      title: 'Právne informácie | Bc. Kamil Frajtko',
      description:
        'Základné právne a identifikačné informácie o prevádzkovateľovi webu a charaktere poskytovaných služieb.',
      keywords: ['právne informácie', 'identifikačné údaje', 'prevádzkovateľ webu', 'elektronický obchod'],
      breadcrumb: 'Právne informácie',
    },
    terms: {
      title: 'Podmienky používania | Bc. Kamil Frajtko',
      description:
        'Podmienky používania webu, odosielania dopytov a základné pravidlá práce s obsahom stránky.',
      keywords: ['podmienky používania', 'pravidlá webu', 'podmienky dopytu'],
      breadcrumb: 'Podmienky používania',
    },
    notFound: {
      title: '404 | Bc. Kamil Frajtko',
      description:
        'Hľadaná stránka sa nenašla. Vráťte sa na hlavnú stránku alebo ma kontaktujte a pomôžem Vám nájsť správny smer.',
      keywords: ['404', 'stránka sa nenašla'],
      breadcrumb: '404',
    },
  },
  cs: {
    home: {
      title: 'Tvorba webů a e-shopů na míru | Bc. Kamil Frajtko',
      description:
        'Tvorba webů, e-shopů, automatizací a digitálních řešení na míru pro firmy, které chtějí funkční web, online prodej a lepší procesy.',
      keywords: [
        'tvorba webů',
        'tvorba e-shopů',
        'web na míru',
        'automatizace procesů',
        'digitální řešení na míru',
        'programování webů',
      ],
      breadcrumb: 'Domů',
    },
    services: {
      title: 'Služby: weby, e-shopy a automatizace | Bc. Kamil Frajtko',
      description:
        'Weby na míru, e-shopy, automatizace a digitální řešení navržené tak, aby fungovaly po technické i byznysové stránce.',
      keywords: ['weby na míru', 'e-shopy', 'automatizace', 'digitální řešení', 'interní systémy'],
      breadcrumb: 'Služby',
    },
    pricing: {
      title: 'Orientační ceny tvorby webů a e-shopů | Bc. Kamil Frajtko',
      description:
        'Orientační startovní ceny pro weby, e-shopy, automatizace a řešení na míru. Každý projekt naceňuji individuálně podle rozsahu a cíle.',
      keywords: ['cena webu', 'cena e-shopu', 'orientační ceny webu', 'web na míru cena'],
      breadcrumb: 'Orientační ceny',
    },
    about: {
      title: 'O mně | Bc. Kamil Frajtko',
      description:
        'Bc. Kamil Frajtko, digitální konzultant a developer. Propojuji technické řešení, funkční design a byznysový pohled.',
      keywords: ['digitální konzultant', 'web developer', 'freelancer programátor', 'o mně'],
      breadcrumb: 'O mně',
    },
    contact: {
      title: 'Kontakt | Bc. Kamil Frajtko',
      description:
        'Kontaktujte mě kvůli webu, e-shopu, automatizaci nebo řešení na míru. Odpovídám zpravidla do 1 až 2 pracovních dnů.',
      keywords: ['kontakt web developer', 'poptávka web na míru', 'kontakt e-shop'],
      breadcrumb: 'Kontakt',
    },
    projects: {
      title: 'Vybrané projekty webů a e-shopů | Bc. Kamil Frajtko',
      description:
        'Výběr webů, e-shopů a digitálních řešení, na kterých jsem pracoval. Ukázky projektů, designu a funkcionalit.',
      keywords: ['portfolio web developer', 'projekty webů', 'ukázky e-shopů'],
      breadcrumb: 'Vybrané projekty',
    },
    privacy: {
      title: 'Ochrana osobních údajů | Bc. Kamil Frajtko',
      description:
        'Informace o zpracování osobních údajů, kontaktním formuláři, analytice a právech subjektů údajů podle GDPR.',
      keywords: ['ochrana osobních údajů', 'GDPR', 'zpracování údajů', 'kontaktní formulář'],
      breadcrumb: 'Ochrana osobních údajů',
    },
    cookies: {
      title: 'Cookies | Bc. Kamil Frajtko',
      description:
        'Informace o cookies, Google Analytics 4 a možnostech správy souhlasu na tomto webu.',
      keywords: ['cookies', 'google analytics', 'souhlas s cookies', 'nastavení cookies'],
      breadcrumb: 'Cookies',
    },
    legalInfo: {
      title: 'Právní informace | Bc. Kamil Frajtko',
      description:
        'Základní právní a identifikační informace o provozovateli webu a charakteru poskytovaných služeb.',
      keywords: ['právní informace', 'identifikační údaje', 'provozovatel webu'],
      breadcrumb: 'Právní informace',
    },
    terms: {
      title: 'Podmínky používání | Bc. Kamil Frajtko',
      description:
        'Podmínky používání webu, odesílání poptávek a základní pravidla práce s obsahem stránky.',
      keywords: ['podmínky používání', 'pravidla webu', 'podmínky poptávky'],
      breadcrumb: 'Podmínky používání',
    },
    notFound: {
      title: '404 | Bc. Kamil Frajtko',
      description:
        'Hledaná stránka nebyla nalezena. Vraťte se na hlavní stránku nebo mě kontaktujte a pomohu Vám najít správný směr.',
      keywords: ['404', 'stránka nenalezena'],
      breadcrumb: '404',
    },
  },
  en: {
    home: {
      title: 'Custom websites and e-commerce | Bc. Kamil Frajtko',
      description:
        'Custom websites, e-commerce, automations and digital solutions for companies that need a functional website, online sales and better processes.',
      keywords: [
        'custom websites',
        'e-commerce development',
        'web development',
        'process automation',
        'digital solutions',
      ],
      breadcrumb: 'Home',
    },
    services: {
      title: 'Services: websites, e-commerce and automation | Bc. Kamil Frajtko',
      description:
        'Custom websites, e-commerce, automation and digital solutions designed to work from both a technical and business perspective.',
      keywords: ['custom websites', 'e-commerce', 'automation', 'digital solutions', 'internal systems'],
      breadcrumb: 'Services',
    },
    pricing: {
      title: 'Indicative pricing for websites and e-commerce | Bc. Kamil Frajtko',
      description:
        'Indicative starting prices for websites, e-commerce, automation and custom solutions. Every project is priced individually based on scope and goals.',
      keywords: ['website pricing', 'e-commerce pricing', 'custom web development pricing'],
      breadcrumb: 'Indicative pricing',
    },
    about: {
      title: 'About me | Bc. Kamil Frajtko',
      description:
        'Bc. Kamil Frajtko, digital consultant and developer. I combine technical solutions, functional design and business thinking.',
      keywords: ['digital consultant', 'web developer', 'freelance developer', 'about me'],
      breadcrumb: 'About me',
    },
    contact: {
      title: 'Contact | Bc. Kamil Frajtko',
      description:
        'Get in touch about a website, e-commerce, automation or a custom solution. I usually reply within 1 to 2 business days.',
      keywords: ['contact web developer', 'website inquiry', 'e-commerce inquiry'],
      breadcrumb: 'Contact',
    },
    projects: {
      title: 'Selected web and e-commerce projects | Bc. Kamil Frajtko',
      description:
        'A selection of websites, e-commerce projects and digital solutions I have worked on. Project examples, design and functionality.',
      keywords: ['web developer portfolio', 'selected projects', 'e-commerce portfolio'],
      breadcrumb: 'Selected projects',
    },
    privacy: {
      title: 'Privacy policy | Bc. Kamil Frajtko',
      description:
        'Information about personal data processing, the contact form, analytics and data subject rights under GDPR.',
      keywords: ['privacy policy', 'GDPR', 'data processing', 'contact form'],
      breadcrumb: 'Privacy policy',
    },
    cookies: {
      title: 'Cookies | Bc. Kamil Frajtko',
      description:
        'Information about cookies, Google Analytics 4 and consent management on this website.',
      keywords: ['cookies', 'google analytics', 'cookie consent', 'cookie settings'],
      breadcrumb: 'Cookies',
    },
    legalInfo: {
      title: 'Legal information | Bc. Kamil Frajtko',
      description:
        'Basic legal and identification information about the website operator and the nature of the services presented on the website.',
      keywords: ['legal information', 'website operator', 'identification details'],
      breadcrumb: 'Legal information',
    },
    terms: {
      title: 'Terms of use | Bc. Kamil Frajtko',
      description:
        'Terms of use for the website, inquiry submission and the basic rules for working with the site content.',
      keywords: ['terms of use', 'website terms', 'inquiry terms'],
      breadcrumb: 'Terms of use',
    },
    notFound: {
      title: '404 | Bc. Kamil Frajtko',
      description:
        'The page you are looking for could not be found. Go back to the homepage or contact me and I will point you in the right direction.',
      keywords: ['404', 'page not found'],
      breadcrumb: '404',
    },
  },
  de: {
    home: {
      title: 'Individuelle Websites und E-Commerce | Bc. Kamil Frajtko',
      description:
        'Individuelle Websites, E-Commerce, Automatisierungen und digitale Lösungen für Unternehmen, die eine funktionierende Website, Online-Verkauf und bessere Prozesse brauchen.',
      keywords: [
        'Webentwicklung',
        'E-Commerce Entwicklung',
        'individuelle Website',
        'Prozessautomatisierung',
        'digitale Lösungen',
      ],
      breadcrumb: 'Startseite',
    },
    services: {
      title: 'Leistungen: Websites, E-Commerce und Automatisierung | Bc. Kamil Frajtko',
      description:
        'Individuelle Websites, E-Commerce, Automatisierung und digitale Lösungen mit technischem und geschäftlichem Fokus.',
      keywords: ['Websites', 'E-Commerce', 'Automatisierung', 'digitale Lösungen', 'interne Systeme'],
      breadcrumb: 'Leistungen',
    },
    pricing: {
      title: 'Orientierungspreise für Websites und E-Commerce | Bc. Kamil Frajtko',
      description:
        'Orientierende Startpreise für Websites, E-Commerce, Automatisierung und individuelle Lösungen. Jedes Projekt wird individuell kalkuliert.',
      keywords: ['Webdesign Preise', 'E-Commerce Preise', 'individuelle Webentwicklung Preise'],
      breadcrumb: 'Orientierungspreise',
    },
    about: {
      title: 'Über mich | Bc. Kamil Frajtko',
      description:
        'Bc. Kamil Frajtko, Digitalberater und Entwickler. Ich verbinde technische Lösungen, funktionales Design und geschäftliche Perspektive.',
      keywords: ['Digitalberater', 'Webentwickler', 'Freelancer Entwickler', 'über mich'],
      breadcrumb: 'Über mich',
    },
    contact: {
      title: 'Kontakt | Bc. Kamil Frajtko',
      description:
        'Kontaktieren Sie mich wegen einer Website, eines E-Commerce Projekts, einer Automatisierung oder einer individuellen Lösung.',
      keywords: ['Kontakt Webentwickler', 'Website Anfrage', 'E-Commerce Anfrage'],
      breadcrumb: 'Kontakt',
    },
    projects: {
      title: 'Ausgewählte Web- und E-Commerce-Projekte | Bc. Kamil Frajtko',
      description:
        'Eine Auswahl an Websites, E-Commerce-Projekten und digitalen Lösungen, an denen ich gearbeitet habe.',
      keywords: ['Portfolio Webentwickler', 'ausgewählte Projekte', 'E-Commerce Portfolio'],
      breadcrumb: 'Ausgewählte Projekte',
    },
    privacy: {
      title: 'Datenschutz | Bc. Kamil Frajtko',
      description:
        'Informationen zur Verarbeitung personenbezogener Daten, zum Kontaktformular, zur Analyse und zu den Rechten der betroffenen Personen nach DSGVO.',
      keywords: ['Datenschutz', 'DSGVO', 'Datenverarbeitung', 'Kontaktformular'],
      breadcrumb: 'Datenschutz',
    },
    cookies: {
      title: 'Cookies | Bc. Kamil Frajtko',
      description:
        'Informationen zu Cookies, Google Analytics 4 und zur Verwaltung der Einwilligung auf dieser Website.',
      keywords: ['Cookies', 'Google Analytics', 'Cookie-Einwilligung', 'Cookie-Einstellungen'],
      breadcrumb: 'Cookies',
    },
    legalInfo: {
      title: 'Rechtliche Informationen | Bc. Kamil Frajtko',
      description:
        'Grundlegende rechtliche und identifizierende Informationen über den Betreiber der Website und die Art der angebotenen Leistungen.',
      keywords: ['Rechtliche Informationen', 'Website-Betreiber', 'Identifikationsdaten'],
      breadcrumb: 'Rechtliche Informationen',
    },
    terms: {
      title: 'Nutzungsbedingungen | Bc. Kamil Frajtko',
      description:
        'Nutzungsbedingungen für die Website, das Senden von Anfragen und die Grundregeln für die Nutzung der Inhalte.',
      keywords: ['Nutzungsbedingungen', 'Website-Regeln', 'Anfragebedingungen'],
      breadcrumb: 'Nutzungsbedingungen',
    },
    notFound: {
      title: '404 | Bc. Kamil Frajtko',
      description:
        'Die gesuchte Seite wurde nicht gefunden. Kehren Sie zur Startseite zurück oder kontaktieren Sie mich.',
      keywords: ['404', 'Seite nicht gefunden'],
      breadcrumb: '404',
    },
  },
  pl: {
    home: {
      title: 'Strony internetowe i e-commerce na zamówienie | Bc. Kamil Frajtko',
      description:
        'Tworzenie stron internetowych, e-commerce, automatyzacji i rozwiązań cyfrowych dla firm, które chcą funkcjonalnej strony, sprzedaży online i lepszych procesów.',
      keywords: [
        'tworzenie stron internetowych',
        'e-commerce',
        'strona na zamówienie',
        'automatyzacja procesów',
        'rozwiązania cyfrowe',
      ],
      breadcrumb: 'Start',
    },
    services: {
      title: 'Usługi: strony, e-commerce i automatyzacja | Bc. Kamil Frajtko',
      description:
        'Strony na zamówienie, e-commerce, automatyzacja i rozwiązania cyfrowe zaprojektowane z perspektywy technicznej i biznesowej.',
      keywords: ['strony internetowe', 'e-commerce', 'automatyzacja', 'rozwiązania cyfrowe', 'systemy wewnętrzne'],
      breadcrumb: 'Usługi',
    },
    pricing: {
      title: 'Orientacyjne ceny stron i e-commerce | Bc. Kamil Frajtko',
      description:
        'Orientacyjne ceny startowe dla stron internetowych, e-commerce, automatyzacji i rozwiązań na zamówienie. Każdy projekt wyceniam indywidualnie.',
      keywords: ['cena strony internetowej', 'cena e-commerce', 'wycena strony na zamówienie'],
      breadcrumb: 'Orientacyjne ceny',
    },
    about: {
      title: 'O mnie | Bc. Kamil Frajtko',
      description:
        'Bc. Kamil Frajtko, konsultant cyfrowy i developer. Łączę rozwiązania techniczne, funkcjonalny design i spojrzenie biznesowe.',
      keywords: ['konsultant cyfrowy', 'web developer', 'freelancer programista', 'o mnie'],
      breadcrumb: 'O mnie',
    },
    contact: {
      title: 'Kontakt | Bc. Kamil Frajtko',
      description:
        'Skontaktuj się ze mną w sprawie strony internetowej, e-commerce, automatyzacji lub rozwiązania na zamówienie.',
      keywords: ['kontakt web developer', 'zapytanie o stronę', 'zapytanie o e-commerce'],
      breadcrumb: 'Kontakt',
    },
    projects: {
      title: 'Wybrane projekty stron i e-commerce | Bc. Kamil Frajtko',
      description:
        'Wybór stron internetowych, projektów e-commerce i rozwiązań cyfrowych, nad którymi pracowałem.',
      keywords: ['portfolio web developer', 'wybrane projekty', 'portfolio e-commerce'],
      breadcrumb: 'Wybrane projekty',
    },
    privacy: {
      title: 'Polityka prywatności | Bc. Kamil Frajtko',
      description:
        'Informacje o przetwarzaniu danych osobowych, formularzu kontaktowym, analityce oraz prawach osób, których dane dotyczą, zgodnie z GDPR.',
      keywords: ['polityka prywatności', 'GDPR', 'przetwarzanie danych', 'formularz kontaktowy'],
      breadcrumb: 'Polityka prywatności',
    },
    cookies: {
      title: 'Cookies | Bc. Kamil Frajtko',
      description:
        'Informacje o cookies, Google Analytics 4 oraz zarządzaniu zgodą na tej stronie.',
      keywords: ['cookies', 'google analytics', 'zgoda na cookies', 'ustawienia cookies'],
      breadcrumb: 'Cookies',
    },
    legalInfo: {
      title: 'Informacje prawne | Bc. Kamil Frajtko',
      description:
        'Podstawowe informacje prawne i identyfikacyjne o operatorze strony oraz charakterze prezentowanych usług.',
      keywords: ['informacje prawne', 'operator strony', 'dane identyfikacyjne'],
      breadcrumb: 'Informacje prawne',
    },
    terms: {
      title: 'Warunki korzystania | Bc. Kamil Frajtko',
      description:
        'Warunki korzystania ze strony, wysyłania zapytań oraz podstawowe zasady korzystania z treści serwisu.',
      keywords: ['warunki korzystania', 'zasady strony', 'warunki zapytania'],
      breadcrumb: 'Warunki korzystania',
    },
    notFound: {
      title: '404 | Bc. Kamil Frajtko',
      description:
        'Szukana strona nie została znaleziona. Wróć na stronę główną lub skontaktuj się ze mną.',
      keywords: ['404', 'strona nie znaleziona'],
      breadcrumb: '404',
    },
  },
};

function normalizeSiteUrl(url: string) {
  return url.replace(/\/+$/, '');
}

function normalizeBasePath(basePath: string) {
  if (!basePath || basePath === '/') {
    return '';
  }

  return `/${basePath.replace(/^\/+|\/+$/g, '')}`;
}

function normalizePathname(pathname: string) {
  const withoutBase =
    BASE_PATH && pathname.startsWith(BASE_PATH) ? pathname.slice(BASE_PATH.length) || '/' : pathname;
  const withoutTrailingSlash = withoutBase.length > 1 ? withoutBase.replace(/\/+$/, '') : withoutBase;

  return withoutTrailingSlash || '/';
}

function buildAbsoluteUrl(pathname: string) {
  const normalizedPath = pathname === '/' ? '' : pathname;

  return `${SITE_URL}${BASE_PATH}${normalizedPath}`;
}

function resolvePageKey(pathname: string): PageKey {
  const normalized = normalizePathname(pathname);

  switch (normalized) {
    case PATHS.home:
      return 'home';
    case PATHS.sluzby:
      return 'services';
    case PATHS.cennik:
      return 'pricing';
    case PATHS.omne:
      return 'about';
    case PATHS.kontakt:
      return 'contact';
    case PATHS.projekty:
      return 'projects';
    case PATHS.ochranaOsobnychUdajov:
      return 'privacy';
    case PATHS.cookies:
      return 'cookies';
    case PATHS.pravneInformacie:
      return 'legalInfo';
    case PATHS.podmienkyPouzivania:
      return 'terms';
    case '/404':
    case '/404.html':
      return 'notFound';
    default:
      return 'notFound';
  }
}

function getCanonicalPath(pageKey: PageKey) {
  switch (pageKey) {
    case 'home':
      return PATHS.home;
    case 'services':
      return PATHS.sluzby;
    case 'pricing':
      return PATHS.cennik;
    case 'about':
      return PATHS.omne;
    case 'contact':
      return PATHS.kontakt;
    case 'projects':
      return PATHS.projekty;
    case 'privacy':
      return PATHS.ochranaOsobnychUdajov;
    case 'cookies':
      return PATHS.cookies;
    case 'legalInfo':
      return PATHS.pravneInformacie;
    case 'terms':
      return PATHS.podmienkyPouzivania;
    default:
      return '/404';
  }
}

function getBreadcrumbItems(language: AppLanguage, pageKey: PageKey, canonicalUrl: string) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: HOME_LABELS[language],
      item: buildAbsoluteUrl(PATHS.home),
    },
  ];

  if (pageKey !== 'home') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: SEO_COPY[language][pageKey].breadcrumb,
      item: canonicalUrl,
    });
  }

  return items;
}

function getPageType(pageKey: PageKey) {
  switch (pageKey) {
    case 'about':
      return 'AboutPage';
    case 'contact':
      return 'ContactPage';
    case 'projects':
      return 'CollectionPage';
    default:
      return 'WebPage';
  }
}

function createStructuredData(language: AppLanguage, pageKey: PageKey, seo: SeoData) {
  const professionalServiceNeeded = pageKey !== 'notFound';

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}${BASE_PATH || ''}#website`,
      url: buildAbsoluteUrl(PATHS.home),
      name: BRAND_NAME,
      inLanguage: language,
      description: SEO_COPY[language].home.description,
    },
    {
      '@type': 'Person',
      '@id': `${SITE_URL}${BASE_PATH || ''}#person`,
      name: BRAND_NAME,
      url: buildAbsoluteUrl(PATHS.home),
      image: buildAbsoluteUrl('/images/kamil-hero.webp'),
      jobTitle: PROFESSIONAL_SERVICE_LABELS[language],
      description: SEO_COPY[language].about.description,
      sameAs: SOCIAL_LINKS,
      telephone: '+421905929167',
      email: 'kontakt@kamilfrajtko.sk',
      knowsAbout: [...SEO_COPY[language].services.keywords, ...SPECIALIZATIONS],
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${seo.canonicalUrl}#breadcrumb`,
      itemListElement: getBreadcrumbItems(language, pageKey, seo.canonicalUrl),
    },
    {
      '@type': getPageType(pageKey),
      '@id': `${seo.canonicalUrl}#webpage`,
      url: seo.canonicalUrl,
      name: seo.title,
      description: seo.description,
      inLanguage: language,
      isPartOf: {
        '@id': `${SITE_URL}${BASE_PATH || ''}#website`,
      },
      about: {
        '@id': `${SITE_URL}${BASE_PATH || ''}#person`,
      },
    },
  ];

  if (pageKey === 'projects') {
    graph.push({
      '@type': 'ItemList',
      '@id': `${seo.canonicalUrl}#portfolio-list`,
      name: SEO_COPY[language].projects.breadcrumb,
      itemListOrder: 'http://schema.org/ItemListOrderAscending',
    });
  }

  if (professionalServiceNeeded) {
    graph.push({
      '@type': 'ProfessionalService',
      '@id': `${SITE_URL}${BASE_PATH || ''}#service`,
      name: BRAND_NAME,
      url: buildAbsoluteUrl(PATHS.home),
      image: buildAbsoluteUrl('/images/kamil-hero.webp'),
      description: SEO_COPY[language].home.description,
      areaServed: ['SK', 'CZ', 'DE', 'PL', 'EU'],
      availableLanguage: ['sk', 'cs', 'en', 'de', 'pl'],
      sameAs: SOCIAL_LINKS,
      email: 'kontakt@kamilfrajtko.sk',
      telephone: '+421905929167',
      serviceType: [...SPECIALIZATIONS, 'Web development', 'E-commerce development', 'Process automation', 'Custom digital solutions'],
    });
  }

  return graph;
}

function ensureMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function ensureLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

export function getSeoData(pathname: string, language: AppLanguage): SeoData {
  const pageKey = resolvePageKey(pathname);
  const copy = SEO_COPY[language][pageKey];
  const canonicalUrl = buildAbsoluteUrl(getCanonicalPath(pageKey));
  const image = buildAbsoluteUrl('/images/kamil-hero.webp');
  const robots = pageKey === 'notFound' ? NOT_FOUND_ROBOTS : DEFAULT_ROBOTS;

  const seo: SeoData = {
    title: copy.title,
    description: copy.description,
    keywords: copy.keywords.join(', '),
    canonicalUrl,
    image,
    robots,
    ogLocale: LOCALE_CODES[language],
    structuredData: [],
  };

  seo.structuredData = createStructuredData(language, pageKey, seo);

  return seo;
}

export function applySeoToDocument(pathname: string, language: AppLanguage) {
  const seo = getSeoData(pathname, language);

  document.title = seo.title;

  ensureMeta('name', 'description', seo.description);
  ensureMeta('name', 'keywords', seo.keywords);
  ensureMeta('name', 'robots', seo.robots);
  ensureMeta('name', 'googlebot', seo.robots);
  ensureMeta('name', 'author', BRAND_NAME);
  ensureMeta('name', 'creator', BRAND_NAME);
  ensureMeta('name', 'publisher', BRAND_NAME);
  ensureMeta('name', 'theme-color', '#0D0D0D');

  if (__GOOGLE_SITE_VERIFICATION__) {
    ensureMeta('name', 'google-site-verification', __GOOGLE_SITE_VERIFICATION__);
  }

  ensureLink('canonical', seo.canonicalUrl);

  ensureMeta('property', 'og:site_name', BRAND_NAME);
  ensureMeta('property', 'og:type', 'website');
  ensureMeta('property', 'og:locale', seo.ogLocale);
  ensureMeta('property', 'og:url', seo.canonicalUrl);
  ensureMeta('property', 'og:title', seo.title);
  ensureMeta('property', 'og:description', seo.description);
  ensureMeta('property', 'og:image', seo.image);
  ensureMeta('property', 'og:image:alt', BRAND_NAME);

  ensureMeta('name', 'twitter:card', 'summary_large_image');
  ensureMeta('name', 'twitter:title', seo.title);
  ensureMeta('name', 'twitter:description', seo.description);
  ensureMeta('name', 'twitter:image', seo.image);
  ensureMeta('name', 'twitter:image:alt', BRAND_NAME);

  let script = document.head.querySelector<HTMLScriptElement>('script[data-seo-jsonld="true"]');

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seoJsonld = 'true';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': seo.structuredData,
  });
}
